// /Users/donovan68/dva-frontend-vite/src/pages/pro/RoadmapPage.tsx
// Step 3: Approval & Execution - Roadmap Planning view (multi-use-case implementation timeline)
// UPDATED: New layout - Header above journey bar, breadcrumbs removed

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Users, CheckCircle2, Clock, MapPin, GitBranch, FileCheck } from "lucide-react";
import { ProProgressPathV3 } from "../../components/ProProgressPathV3";
import ProJourneyPageHeader from "../../components/journey/pro/ProJourneyPageHeader";
import ProspectPanel from "../../components/ProspectPanel";
import { Step3TabNav } from "../../components/Step3TabNav";
import { MessagesContent } from "../../components/MessagesContent";
import { FreemiumCard } from "../../components/FreemiumCard";
import { useFilter } from "../../contexts/FilterContext";
import { usePageContext } from "../../contexts/PageContext";
import { useClient } from "../../contexts/ClientContext";
import { useProspectList } from "../../hooks/useProspectList";
import { convertToProspectPanelFormat } from "../../utils/prospectUtils";

export default function RoadmapPage() {
  const navigate = useNavigate();
  const { activeFilter, setActiveFilter } = useFilter();
  const { setPageContext } = usePageContext();
  const { clientId } = useClient();
  const { prospects: rawProspects, updateStatus } = useProspectList(clientId || '');
  const prospects = convertToProspectPanelFormat(rawProspects);
  const messageCount = 4;

  // Handlers for ProspectPanel
  const handleProspectRemove = async (prospectId: string) => {
    if (!clientId) return;
    try {
      await updateStatus(Number(prospectId), 'qualified');
    } catch (err) {
      console.error('Failed to remove prospect:', err);
    }
  };

  const handleProfileClick = (prospectId: string) => {
    navigate(`/usecase/${prospectId}`);
  };

  const handleRoiClick = (prospectId: string) => {
    navigate(`/pro/analysis/${prospectId}`);
  };

  const handlePlanClick = (prospectId: string) => {
    navigate(`/pro/roadmap-planning`);
  };

  const handleReorder = (reordered: any[]) => {
    console.log('Prospects reordered:', reordered);
  };

  // Set page context - REMOVED breadcrumbs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [],
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  // TODO: Replace with real data from hooks/services
  const personalizationData = {
    strategy: 100,
    capabilities: 90,
    readiness: 80,
    docsCount: 6
  };

  // Handle phase navigation (updated for 3-step system)
  const handlePhaseClick = (phaseId: string) => {
    switch(phaseId) {
      case 'portfolio':
        navigate("/home");
        break;
      case 'analysis':
        navigate("/pro/analysis");
        break;
      case 'approval_execution':
        navigate("/pro/approval");
        break;
      default:
        navigate("/home");
    }
  };

  // DOCUMENTS CONTENT (3 cards for Roadmap)
  const documentsCards = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Implementation Roadmap Templates",
      description: "Proven timeline templates for healthcare analytics deployments.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Phase-based project plans",
        "Milestone tracking frameworks",
        "Resource allocation guides",
        "Risk mitigation strategies"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "3.7 MB"
    },
    {
      icon: <GitBranch className="w-6 h-6" />,
      title: "Project Management Guide",
      description: "Comprehensive PM methodologies for data initiatives.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Agile healthcare frameworks",
        "Sprint planning templates",
        "Stakeholder communication plans",
        "Dependency mapping tools"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "4.5 MB"
    },
    {
      icon: <FileCheck className="w-6 h-6" />,
      title: "Change Management Toolkit",
      description: "Resources for driving organizational adoption and change.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Staff training programs",
        "Communication templates",
        "Adoption measurement tools",
        "Resistance mitigation tactics"
      ],
      isPremium: false,
      fileType: "PPTX",
      fileSize: "5.8 MB"
    },
  ];

  const milestones = [
    { id: 1, title: "Governance Charter Approved", date: "Q1 2025", owner: "CDO", status: "completed" },
    { id: 2, title: "Analytics Pilot Launch", date: "Q2 2025", owner: "Analytics Team", status: "in-progress" },
    { id: 3, title: "Full Deployment", date: "Q3 2025", owner: "IT", status: "upcoming" },
    { id: 4, title: "Optimization Phase", date: "Q4 2025", owner: "Operations", status: "upcoming" },
  ];

  const timeline = [
    { quarter: "Q1", initiatives: ["Data Governance", "Quality Measures", "Team Training"], progress: 100 },
    { quarter: "Q2", initiatives: ["Analytics Platform", "Pop Health Dashboard", "Integration"], progress: 65 },
    { quarter: "Q3", initiatives: ["Predictive Models", "Executive Reports", "Automation"], progress: 20 },
    { quarter: "Q4", initiatives: ["Optimization", "Scale", "Advanced Analytics"], progress: 0 },
  ];

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      {/* NEW LAYOUT ORDER: Tabs → Page Header → Journey Bar → Step3 Tabs → Content */}
      
      {/* 0. Content Tabs (Tools / Documents / Messages) - Same position as header tabs */}
      <div className="h-10 flex items-center justify-end gap-2 pt-3 mb-0.5">
        <nav className="flex items-center gap-2" aria-label="Section navigation">
          {['Tools', 'Documents', 'Messages'].map((tab) => {
            const active = tab === activeFilter;
            const isMessages = tab === 'Messages';
            const showBadge = isMessages && messageCount > 0;
            
            return (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                  ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
                aria-current={active ? "page" : undefined}
              >
                {tab}
                {showBadge && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                    {messageCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
      
      {/* 1. Page Header */}
      <div className="mb-6 -mt-2">
        <ProJourneyPageHeader
          stepNumber={3}
          stepName="Roadmap"
          description="Build multi-use-case implementation timeline with milestones, dependencies, and resource planning."
        />
      </div>

      {/* 2. Journey Bar */}
      <div className="mb-6">
        <ProProgressPathV3 
          currentPhase="approval_execution"
          personalizationData={personalizationData}
          onPersonalizationClick={() => navigate("/personalize")}
          onPhaseClick={handlePhaseClick}
        />
      </div>

      {/* 3. Step 3 Tab Navigation (Roadmap / Approval / Execution) */}
      <div className="mb-6">
        <Step3TabNav />
      </div>

      {/* 4. Main content with fixed right panel */}
      <div className="flex gap-0">
        {/* LEFT: main content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Conditional content based on activeFilter */}
      {activeFilter === 'Messages' ? (
        <MessagesContent />
      ) : activeFilter === 'Documents' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documentsCards.map((card, idx) => (
            <FreemiumCard
              key={idx}
              icon={card.icon}
              title={card.title}
              description={card.description}
              contentType={card.contentType}
              listItems={card.listItems}
              isPremium={card.isPremium}
            />
          ))}
        </div>
      ) : (
        /* Tools view - Roadmap content (default) */
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl ring-1 ring-[#e5e7eb] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#18365E] mb-6">Quarterly Timeline</h2>
            <div className="space-y-6">
              {timeline.map((q) => (
                <div key={q.quarter} className="border-l-4 border-[#18365E] pl-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-bold text-[#18365E]">{q.quarter} 2025</h3>
                    <span className="text-sm font-semibold text-[#717182]">{q.progress}%</span>
                  </div>
                  <div className="h-2 bg-[#e5e7eb] rounded-full mb-3">
                    <div 
                      className="h-full bg-[#FF6E4C] rounded-full transition-all"
                      style={{ width: `${q.progress}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {q.initiatives.map((init, i) => (
                      <span key={i} className="text-xs px-3 py-1 bg-[#F5F7FA] text-[#18365E] rounded-full font-medium">
                        {init}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl ring-1 ring-[#e5e7eb] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#18365E] mb-4">Upcoming Milestones</h2>
            <div className="space-y-4">
              {milestones.map((m) => (
                <div key={m.id} className="p-4 rounded-xl bg-[#F5F7FA]">
                  <div className="flex items-start gap-3">
                    {m.status === 'completed' ? (
                      <CheckCircle2 className="w-5 h-5 text-[#6FCF97] flex-shrink-0 mt-0.5" />
                    ) : m.status === 'in-progress' ? (
                      <Clock className="w-5 h-5 text-[#FF6E4C] flex-shrink-0 mt-0.5" />
                    ) : (
                      <Calendar className="w-5 h-5 text-[#717182] flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-[#18365E]">{m.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Users className="w-3 h-3 text-[#717182]" />
                        <span className="text-xs text-[#717182]">{m.owner}</span>
                      </div>
                      <span className="text-xs text-[#717182] font-medium">{m.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </>
          )}
        </div>

        {/* RIGHT: Prospect Panel - Fixed */}
        {clientId && (
          <div className="hidden lg:block">
            <ProspectPanel
              prospects={prospects}
              onRemove={handleProspectRemove}
              onProfileClick={handleProfileClick}
              onRoiClick={handleRoiClick}
              onPlanClick={handlePlanClick}
              onReorder={handleReorder}
            />
          </div>
        )}
      </div>
    </main>
  );
}